import {
	Box,
	InputAdornment,
	ListSubheader,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';

import { useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';

import { IMultiSelectOption } from 'common/api/type-def';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';

import { circleDeleteWhiteIcon, downArrowIcon } from '../../assets/images';

import styles from './MultipleSelectCheckmarks.module.scss';

const { checkboxCheckedColor, checkboxUncheckedColor, borderColor } = styles;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const CustomDropdownUpIcon = () => (
	<Box pr={3} className={styles['select-icon']} data-testid="dropdown-icon">
		<img src={downArrowIcon} alt="arrow" />
	</Box>
);
const CustomDropdownDownIcon = () => (
	<Box pr={3} className={styles['down-icon']} data-testid="dropdown-icon">
		<img src={downArrowIcon} alt="arrow" />
	</Box>
);

type IMultipleSelectProps = Omit<SelectProps, 'onChange'> & {
	className?: string;
	isClearFilter: boolean;
	isSearchBox: boolean;
	optionsProps: ISelectOption[];
	inputLabel: string;
	onSelectedChange: (values: IMultiSelectOption[]) => void;
	onClearBtnClick?: boolean;
	clearable?: boolean;
	isInputLabel: boolean;
};

export interface ISelectOption {
	label: string;
	value: string;
	subOptions?: Option[];
	checked?: boolean;
	indeterminate?: boolean;
	type?: string;
	combined?: boolean;
	combinedOptions?: string[];
}
type Option = {
	label: string;
	value: string;
	checked?: boolean;
};

const MultipleSelectCheckmarks = ({
	className,
	isClearFilter,
	optionsProps,
	isSearchBox,
	inputLabel,
	onSelectedChange,
	onClearBtnClick,
	clearable,
	isInputLabel,
	...otherProps
}: IMultipleSelectProps) => {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [options, setOptions] = useState<ISelectOption[]>(optionsProps);
	const [selected, setSelected] = useState<string[]>([]);
	const [count, setCount] = useState<number>(0);
	const [menuItems, setMenuItems] = useState<ISelectOption[]>(optionsProps);
	const [searchValue, setSearchValue] = useState<string>('');
	const searchvalueRef = useRef<any>(null);
	const selectedStyles = {
		select: {
			'&.Mui-selected': {
				borderColor: checkboxCheckedColor, // change this to your desired color
			},
			'.MuiOutlinedInput-notchedOutline': {
				borderColor: checkboxCheckedColor,
			},
			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				bordercolor: checkboxCheckedColor,
			},
			'&:hover .MuiOutlinedInput-notchedOutline': {
				borderColor: checkboxCheckedColor,
			},
			'&:hover:not .MuiOutlinedInput-notchedOutline': {
				borderColor: checkboxCheckedColor,
			},
		},
	};

	const style = {
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				border: `1px solid ${borderColor}`,
				borderRadius: '5px',
				opacity: 0.3,
			},

			'&.Mui-focused fieldset': {
				border: `1px solid ${borderColor}`,
				borderRadius: '5px',
			},
		},
	};
	const changeSearchInput = (event: any) => {
		const searchText = event.target.value;
		setSearchValue(searchText);
		if (searchText.trim() !== '') {
			const filteredOptionsVal = optionsProps.filter((option: any) => {
				// Check if the option label matches the search text
				const labelMatch =
					option.label !== null &&
					option.label.toLowerCase().includes(searchText.toLowerCase());
				// Check if any of the suboption values match the search text
				const subOptionMatch =
					option.subOptions &&
					option.subOptions.some((subOption: any) =>
						subOption.value.toLowerCase().includes(searchText.toLowerCase()),
					);
				// Return true if either of the conditions is true
				return labelMatch || subOptionMatch;
			});
			setOptions(filteredOptionsVal);
		} else {
			setOptions(menuItems);
		}
	};

	const debouncedSearchHandler = debounce(changeSearchInput, 500);

	const handleClearText = () => {
		if (searchvalueRef?.current) {
			searchvalueRef.current.value = '';
		}
		setOptions(optionsProps);
		setSearchValue('');
	};

	useEffect(() => {
		if (optionsProps) {
			setOptions(optionsProps);
			setMenuItems(optionsProps);
		}
	}, [optionsProps]);

	const handleParentChange = (option: any, checked: boolean) => {
		setMenuItems((prevstate: ISelectOption[]) => {
			const updatedOptions = prevstate?.map((element: any) => {
				if (element.value === option.value) {
					const updatedSubOptions = element.subOptions?.map(
						(subOption: any) => ({
							...subOption,
							checked,
						}),
					);
					return {
						...element,
						checked,
						indeterminate: checked ? false : null,
						subOptions: updatedSubOptions,
					};
				}
				return element;
			});
			return [...updatedOptions];
		});
	};

	const handleChildChange = (
		option: ISelectOption,
		subOption: Option,
		checked: boolean,
	) => {
		setMenuItems((prevstate: ISelectOption[]) => {
			const childUpdatedOptions = prevstate?.map((element: any) => {
				if (element.value === option.value) {
					const updatedSubOptions = element.subOptions?.map(
						(childEle: any) => ({
							...childEle,
							...(childEle.value === subOption.value && { checked }),
						}),
					);
					const isParentChecked = updatedSubOptions?.every(
						(eleOption: Option) => eleOption.checked,
					);
					const isParentIndeterminate = updatedSubOptions?.some(
						(eleOption: Option) => eleOption.checked,
					);
					return {
						...element,
						checked: isParentChecked,
						indeterminate: !isParentChecked && isParentIndeterminate,
						subOptions: updatedSubOptions,
					};
				}
				return element;
			});
			return [...childUpdatedOptions];
		});
	};

	useEffect(() => {
		const updatedSelected: IMultiSelectOption[] = [];
		menuItems.forEach((ele: any) => {
			if (ele.checked) updatedSelected.push(ele);
			if (ele.indeterminate && ele.subOptions) {
				const subOptions = ele.subOptions.filter(
					(ele: { checked: any }) => ele.checked,
				);
				updatedSelected.push({
					...ele,
					subOptions,
				});
			}
		});
		onSelectedChange(updatedSelected);
		const updatedCount: number = menuItems.filter(
			(option: ISelectOption) =>
				option.checked || option.indeterminate === true,
		).length;
		setCount(updatedCount);
	}, [menuItems]);

	useEffect(() => {
		if (onClearBtnClick) {
			resetFilters();
		}
	}, [onClearBtnClick]);

	const resetFilters = () => {
		setSelected([]);
		setOptions(optionsProps);
		setMenuItems(optionsProps);
		setCount(0);
	};

	const onKeyDown = (e: any) => {
		if (e.key !== 'Escape') {
			e.stopPropagation();
		}
	};

	const rendervalue = count > 0 ? `${count} items selected` : inputLabel;

	const handleParentDivClick = (option: ISelectOption) => {
		const selectedMenuItem: any = menuItems.find(
			(ele) => ele.value === option.value,
		);
		const newValue = !selectedMenuItem?.checked;
		handleParentChange(option, newValue);
	};

	const handleDivClick = (option: ISelectOption, subOption: any) => {
		const selectedMenuItem: any = menuItems.find(
			(ele) => ele.value === option.value,
		);
		const childSubOption = selectedMenuItem?.subOptions.find(
			(ele: Option) => ele.value === subOption.value,
		);
		// Add your logic here to handle the click event
		handleChildChange(option, subOption, !childSubOption.checked);
	};

	return (
		<div>
			{isClearFilter && (
				<div>
					<Typography
						className={`${styles['clear-filter-style']}`}
						data-testid="clear-filter"
						onClick={resetFilters}
					>
						Clear All Filters
					</Typography>
				</div>
			)}
			<div>
				<FormControl className={styles['form-control-style']}>
					{isInputLabel && (
						<InputLabel
							id="demo-multiple-checkbox-label"
							data-testid="label-id"
							sx={{
								'&.MuiInputLabel-root[data-shrink="true"]': {
									marginTop: '1em !important',
								},
								'&.Mui-focused': {
									marginTop: '7px !important',
									color: checkboxCheckedColor,
									borderColor: checkboxCheckedColor,
								},
								'&.Mui-focused:not': {
									marginTop: '10px !important',
									borderColor: checkboxCheckedColor,
								},
								'&:before': {
									borderColor: checkboxCheckedColor,
								},
								'&:after': {
									borderColor: checkboxCheckedColor,
								},
							}}
							className={`${styles['dropdown-select']}`}
						>
							{inputLabel}
						</InputLabel>
					)}
					<Select
						disabled={options.length === 0}
						sx={selectedStyles.select}
						className={className}
						labelId="demo-multiple-checkbox-label"
						id="demo-multiple-checkbox"
						multiple
						value={selected}
						displayEmpty
						renderValue={() => (!isInputLabel ? rendervalue : null)}
						MenuProps={MenuProps}
						variant="outlined"
						inputProps={{
							'data-testid': 'multiple-select-dropdown',
						}}
						IconComponent={
							!dropdownOpen ? CustomDropdownUpIcon : CustomDropdownDownIcon
						}
						onClose={() => setDropdownOpen(false)}
						onOpen={() => setDropdownOpen(true)}
						{...otherProps}
					>
						{isSearchBox && (
							<ListSubheader data-testid="list-subheader">
								<TextField
									inputRef={searchvalueRef}
									className={styles['search-field']}
									autoFocus
									placeholder="Search"
									fullWidth
									onChange={debouncedSearchHandler}
									onKeyDown={onKeyDown}
									InputProps={{
										inputProps: { 'data-testid': 'search-field' },
										startAdornment: (
											<InputAdornment position="start">
												<SearchOutlined
													className={styles['input-search-icon']}
												/>
											</InputAdornment>
										),
										endAdornment: clearable && searchValue && (
											<InputAdornment position="end">
												<button
													type="button"
													tabIndex={0}
													onClick={handleClearText}
													onKeyDown={handleClearText}
													className={styles['cusror-pointer']}
													aria-label="Clear"
													data-testid="circle-close-icon"
												>
													<img src={circleDeleteWhiteIcon} alt="circle-icon" />
												</button>
											</InputAdornment>
										),
									}}
									sx={style}
								/>
							</ListSubheader>
						)}
						{options.length > 0 &&
							options.map((option) => {
								const selectedMenuItem: any = menuItems.find(
									(ele) => ele.value === option.value,
								);
								const temp: any =
									option.subOptions &&
									option?.subOptions?.map((subOption) => {
										const childSubOption = selectedMenuItem?.subOptions.find(
											(ele: Option) => ele.value === subOption.value,
										);
										return (
											<div
												key={subOption.label}
												className={styles['submenu-style']}
												data-testid="child-checkbox-testid"
											>
												<MenuItem
													key={subOption.value}
													value={subOption.value}
													onClick={(event) => {
														event.stopPropagation(); // Stop event propagation to parent elements
														handleDivClick(option, subOption);
													}}
												>
													<Checkbox
														sx={{
															color: checkboxUncheckedColor,
															'&.Mui-checked': { color: checkboxCheckedColor },
														}}
														checked={childSubOption?.checked}
														onChange={(event) => {
															event.stopPropagation();
															handleChildChange(
																option,
																subOption,
																event.target.checked,
															);
														}}
													/>
													<Tooltip
														title={subOption.label}
														arrow
														onClick={(event) => {
															event.stopPropagation(); // Stop event propagation to parent elements
															handleDivClick(option, subOption);
														}}
													>
														<ListItemText
															className={styles['text-limit']}
															primary={subOption.label}
														/>
													</Tooltip>
												</MenuItem>
											</div>
										);
									});
								return (
									<div key={option.value} data-testid="parent-checkbox-testid">
										<MenuItem
											key={option.value}
											value={option.value}
											onClick={(event) => {
												event.stopPropagation();
												handleParentDivClick(option);
											}}
										>
											<Checkbox
												sx={{
													color: checkboxUncheckedColor,
													'&.Mui-checked': { color: checkboxCheckedColor },
													'&.MuiCheckbox-indeterminate': {
														color: checkboxCheckedColor,
													},
												}}
												checked={selectedMenuItem?.checked}
												indeterminate={selectedMenuItem?.indeterminate}
												onChange={(event) => {
													event.stopPropagation();
													handleParentChange(option, event.target.checked);
												}}
											/>
											<Tooltip
												title={option.label}
												arrow
												onClick={(event) => {
													event.stopPropagation();
													handleParentDivClick(option);
												}}
											>
												<ListItemText
													className={styles['text-limit']}
													primary={option.label}
												/>
											</Tooltip>
										</MenuItem>
										{temp}
									</div>
								);
							})}
					</Select>
				</FormControl>
			</div>
		</div>
	);
};

export default MultipleSelectCheckmarks;
